import {
  type CreateOptions,
  ErrorType,
  type UpdateOptions,
} from '@wix/ambassador-forms-v1-validation-result/types';
import { type Form } from '@wix/ambassador-forms-v4-form/types';
import {
  Feature,
  type PremiumRestrictions,
} from '@wix/ambassador-forms-v4-restrictions/types';

import {
  type ValidationError,
  type ValidationResult,
} from '../submission-validation/errors';
import { asRequired } from '../utils/lang';

export const FILE_UPLOAD_FIELD_TYPE = 'FILE_UPLOAD';

type RestrictionValidation = (
  newForm: ValidationForm,
  knownForm: ValidationForm,
) => ValidationError | null;

export function validatePremiumRestrictions(
  restrictions: PremiumRestrictions,
  newSchema: Form,
  action: UpdateOptions | CreateOptions,
): ValidationResult {
  const validations: RestrictionValidation[] = [
    validateFormFieldCount({
      limit: getLimit(restrictions.fieldsLimit?.limit),
    }),
    validateFormStepCount({ limit: getLimit(restrictions.stepsLimit?.limit) }),
    validateFormRulesCount({
      limit: getLimit(restrictions.conditionsLimit?.limit),
    }),
    validateFileUploadFieldAllowed({
      featureRestricted: isFeatureRestricted(getOrEmpty(restrictions.features)),
    }),
  ];

  if (isUpdateAction(action)) {
    const knownForm = asValidationForm(action.currentForm);
    const newForm = asValidationForm(newSchema);
    return {
      errors: getValidationErrors(validations, newForm, knownForm),
    };
  }

  if (isCreateAction(action)) {
    const knownForm = asValidationForm();
    const newForm = asValidationForm(newSchema);
    return {
      errors: getValidationErrors(
        [
          ...validations,
          validateTotalFormCount(
            getLimit(restrictions.formsLimit?.limit),
            asRequired(action.existingFormsCount, action, 'existingFormsCount'),
          ),
        ],
        newForm,
        knownForm,
      ),
    };
  }

  throw new Error('Unknown validation action');
}

function getValidationErrors(
  validations: RestrictionValidation[],
  newForm: ValidationForm,
  knownForm: ValidationForm,
) {
  return validations
    .map((validation) => validation(newForm, knownForm))
    .filter(Boolean)
    .map((err) => err as ValidationError);
}

const validateTotalFormCount =
  (limit: number, formCount: number) => (): ValidationError | null =>
    limitRestricted(limit, formCount, formCount + 1)
      ? formCountError(limit)
      : null;

const validateFormFieldCount =
  ({ limit }: LimitRestriction) =>
  (
    newForm: ValidationForm,
    knownForm: ValidationForm,
  ): ValidationError | null =>
    limitRestricted(
      limit,
      knownForm.inputFieldsCount,
      newForm.inputFieldsCount,
    ) ||
    substitutionRestricted(
      limit,
      knownForm.inputFieldsCount,
      newForm.inputFieldIds,
      knownForm.inputFieldIds,
    )
      ? formFieldCountError(limit)
      : null;

const validateFormStepCount =
  ({ limit }: LimitRestriction) =>
  (
    newForm: ValidationForm,
    knownForm: ValidationForm,
  ): ValidationError | null =>
    limitRestricted(limit, knownForm.stepsCount, newForm.stepsCount) ||
    substitutionRestricted(
      limit,
      knownForm.stepsCount,
      newForm.stepsIds,
      knownForm.stepsIds,
    )
      ? formStepsCountError(limit)
      : null;

const validateFormRulesCount =
  ({ limit }: LimitRestriction) =>
  (
    newForm: ValidationForm,
    knownForm: ValidationForm,
  ): ValidationError | null =>
    limitRestricted(limit, knownForm.rulesCount, newForm.rulesCount) ||
    substitutionRestricted(
      limit,
      knownForm.rulesCount,
      newForm.rulesIds,
      knownForm.rulesIds,
    )
      ? formRulesCountError(limit)
      : null;

const validateFileUploadFieldAllowed =
  ({ featureRestricted }: FeatureRestriction) =>
  (
    newForm: ValidationForm,
    knownForm: ValidationForm,
  ): ValidationError | null => {
    const newFileUploadFields = difference(
      newForm.fileUploadFieldIds,
      knownForm.fileUploadFieldIds,
    );
    return newFileUploadFields.length &&
      featureRestricted(Feature.FILE_UPLOAD_FIELD)
      ? formFileUploadProhibitedError()
      : null;
  };

function difference<T>(current: T[], other: T[]) {
  const otherSet = new Set(other);
  return current.filter((x) => !otherSet.has(x));
}

function limitRestricted(
  limit: number,
  knownAmount: number,
  newAmount: number,
) {
  return newAmount > Math.max(limit, knownAmount);
}

function substitutionRestricted(
  limit: number,
  knownAmount: number,
  newIds: string[],
  knownIds: string[],
) {
  return (
    knownAmount > limit &&
    difference(newIds, knownIds).length &&
    newIds.length > limit
  );
}

const isFeatureRestricted =
  (allowedFeatures: Feature[]) => (expectedFeature: Feature) => {
    return !allowedFeatures.some((feature) => feature === expectedFeature);
  };

function formCountError(limit: number): ValidationError {
  return {
    errorPath: 'form',
    errorType: ErrorType.FORMS_COUNT_RESTRICTIONS_ERROR,
    errorMessage: `Form count reached its limit of ${limit}`,
    params: {
      limit,
    },
  };
}

function formFieldCountError(limit: number): ValidationError {
  return {
    errorPath: 'form',
    errorType: ErrorType.FIELDS_COUNT_RESTRICTIONS_ERROR,
    errorMessage: `Field count reached its limit of ${limit}`,
    params: {
      limit,
    },
  };
}

function formStepsCountError(limit: number): ValidationError {
  return {
    errorPath: 'form',
    errorType: ErrorType.STEPS_COUNT_RESTRICTIONS_ERROR,
    errorMessage: `Steps count reached its limit of ${limit}`,
    params: {
      limit,
    },
  };
}

function formRulesCountError(limit: number): ValidationError {
  return {
    errorPath: 'form',
    errorType: ErrorType.RULES_COUNT_RESTRICTIONS_ERROR,
    errorMessage: `Rules count reached its limit of ${limit}`,
    params: {
      limit,
    },
  };
}

function formFileUploadProhibitedError(): ValidationError {
  return {
    errorPath: 'form',
    errorType: ErrorType.FILE_UPLOAD_RESTRICTIONS_ERROR,
    errorMessage: `File upload field is not enabled for the given site`,
  };
}

interface ValidationForm {
  inputFieldsCount: number;
  stepsCount: number;
  rulesCount: number;
  inputFieldIds: string[];
  stepsIds: string[];
  rulesIds: string[];
  fileUploadFieldIds: string[];
}

function asValidationForm(given?: Form): ValidationForm {
  const form = given ?? {};
  const inputFields = (form.fields ?? []).filter((field) =>
    Boolean(field.target),
  );
  return {
    inputFieldsCount: inputFields.length,
    stepsCount: (form.steps ?? []).length,
    rulesCount: (form.rules ?? []).length,
    inputFieldIds: inputFields.map((_) => _.id!),
    stepsIds: (form.steps ?? []).map((_) => _.id!),
    rulesIds: (form.rules ?? []).map((_) => _.id!),
    fileUploadFieldIds: (form.fields ?? [])
      .filter(
        (field) => (field.view ?? {}).fieldType === FILE_UPLOAD_FIELD_TYPE,
      )
      .map((_) => _.id!),
  };
}

function isUpdateAction(action: any): action is UpdateOptions {
  return 'currentForm' in action;
}

function isCreateAction(action: any): action is CreateOptions {
  return 'existingFormsCount' in action;
}

function getLimit(maybeValue?: number | null | undefined): number {
  return maybeValue ?? Number.MAX_SAFE_INTEGER;
}

function getOrEmpty<T>(maybeValue: T[] | null | undefined): T[] {
  return maybeValue ?? [];
}

interface LimitRestriction {
  limit: number;
}

interface FeatureRestriction {
  featureRestricted: (expectedFeature: Feature) => boolean;
}
